// Client-side party + toast state (Phase 5 Step 16b).
//
// Pure state module — no PixiJS imports. server-message-reducer calls
// into these reducers for partyInvite / partyMemberJoined /
// partyMemberLeft / partyDissolved; the party-ui panel + toast
// renderer subscribe by reading the state each frame. The shape is
// deliberately narrow: no separate "status" column on roster members,
// no leader-exclusive flag — the roster list is the leader's member
// list as delivered by the server, and `leaderId === localPlayerId`
// identifies the leader-side view.
//
// TTL on pending invites is pruned lazily on read (mirrors the Step 11
// server-side pattern) so a stale invite doesn't linger in the UI past
// balance.expedition.partyInviteTtlMs.

import { balance } from "../../shared/balance.ts";

export type PendingInvite = {
  partyId: string;
  leaderId: string;
  leaderName: string;
  invitedAt: number;
  ttlMs: number;
};

export type PartyRosterMember = { memberId: string };

export type PartyRoster = {
  partyId: string;
  leaderId: string;
  members: PartyRosterMember[];
};

export type ToastEntry = {
  id: string;
  text: string;
  // 'partyInvite' is clickable (opens the party panel); 'itemRepaired'
  // (Phase 5 Step 18), 'jobCompleted' (Phase 5 Step 19, replaces the
  // retired workshop-polling UX), and 'listingSold' (Phase 5 Step 21
  // followup — seller-side push applied via market-client-handlers)
  // are informational and the click is a no-op. Widen when Phase 6
  // adds expedition-ready / system notifications.
  kind: "partyInvite" | "itemRepaired" | "jobCompleted" | "listingSold";
  // Auxiliary payload — e.g. partyId for a partyInvite toast so the
  // click handler can scroll the right invite into view.
  contextId?: string;
  spawnedAt: number;
};

export type PartyClientState = {
  pendingInvites: PendingInvite[];
  roster: PartyRoster | null;
  toasts: ToastEntry[];
};

export const TOAST_LIFETIME_MS = 3000; // contracts:allow-literal — 3s toast fade

export function createPartyState(): PartyClientState {
  return { pendingInvites: [], roster: null, toasts: [] };
}

// ---- reducers ----

export function applyPartyInvite(
  state: PartyClientState,
  payload: {
    partyId: string;
    leaderId: string;
    leaderName: string;
    invitedAt: number;
    ttlMs: number;
  },
  now: number,
): void {
  pruneExpiredInvites(state, now);
  // Idempotency: if we already have an invite for this party (retry
  // after reconnect), refresh invitedAt instead of duplicating.
  const existing = state.pendingInvites.find((i) => i.partyId === payload.partyId);
  if (existing !== undefined) {
    existing.invitedAt = payload.invitedAt;
    existing.leaderId = payload.leaderId;
    existing.leaderName = payload.leaderName;
    existing.ttlMs = payload.ttlMs;
    return;
  }
  state.pendingInvites.push({ ...payload });
  state.toasts.push({
    id: `party-invite-${payload.partyId}`,
    text: `Party invite from ${payload.leaderName}`,
    kind: "partyInvite",
    contextId: payload.partyId,
    spawnedAt: now,
  });
}

export function applyPartyMemberJoined(
  state: PartyClientState,
  payload: { partyId: string; leaderId: string; memberId: string },
  localPlayerId: string,
): void {
  // Seed a roster on first join using the authoritative leaderId from
  // the server (Phase 5 Step 21 followup closed the pre-existing
  // localPlayerId placeholder that misrendered the roster for any
  // non-inviter member). If the roster already exists for a different
  // party, ignore the stray event.
  if (state.roster === null) {
    state.roster = {
      partyId: payload.partyId,
      leaderId: payload.leaderId,
      members: [],
    };
  }
  if (state.roster.partyId !== payload.partyId) {
    // Incoming event for a different party — ignore. Happens in a
    // corner case where a stale dissolved-party event arrives after
    // the new-party invite.
    return;
  }
  // Keep leaderId in sync: a partyMemberJoined envelope replayed after
  // a seed-from-partyInvite could carry a refreshed leaderId if the
  // leader role ever transfers (Phase 6 scope; harmless today).
  state.roster.leaderId = payload.leaderId;
  if (state.roster.members.some((m) => m.memberId === payload.memberId)) return;
  state.roster.members.push({ memberId: payload.memberId });

  // A pending invite whose party just formed is resolved: if we're
  // the joining member, clear the matching pending entry.
  if (payload.memberId === localPlayerId) {
    state.pendingInvites = state.pendingInvites.filter((i) => i.partyId !== payload.partyId);
  }
}

export function applyPartyMemberLeft(
  state: PartyClientState,
  payload: { partyId: string; memberId: string },
  localPlayerId: string,
): void {
  if (state.roster === null) return;
  if (state.roster.partyId !== payload.partyId) return;
  state.roster.members = state.roster.members.filter(
    (m) => m.memberId !== payload.memberId,
  );
  if (payload.memberId === localPlayerId) {
    // Local client left — drop the roster entirely.
    state.roster = null;
  }
}

export function applyPartyDissolved(
  state: PartyClientState,
  payload: { partyId: string },
): void {
  if (state.roster?.partyId === payload.partyId) state.roster = null;
  state.pendingInvites = state.pendingInvites.filter(
    (i) => i.partyId !== payload.partyId,
  );
}

// Lazy TTL prune — call from panel reads + before every applyPartyInvite.
// Uses balance.expedition.partyInviteTtlMs as a safety bound even when
// the server's `ttlMs` would say otherwise, because the server's
// invite-registration TTL is the canonical source.
export function pruneExpiredInvites(state: PartyClientState, now: number): void {
  const fallback = balance.expedition.partyInviteTtlMs;
  state.pendingInvites = state.pendingInvites.filter((i) => {
    const ttl = Math.min(i.ttlMs, fallback);
    return now - i.invitedAt <= ttl;
  });
}

// Toasts: filter in place. Same shape as combat-state.tickFeedback.
export function tickToasts(state: PartyClientState, now: number): void {
  if (state.toasts.length === 0) return;
  const cutoff = now - TOAST_LIFETIME_MS;
  let write = 0;
  for (let read = 0; read < state.toasts.length; read++) {
    const entry = state.toasts[read];
    if (entry === undefined) continue;
    if (entry.spawnedAt >= cutoff) state.toasts[write++] = entry;
  }
  state.toasts.length = write;
}

// Consumers call this after the user clicks Accept/Decline so the
// pending row doesn't linger until the TTL expires. Cross-DO accept
// replies already clean up server-side; this is the local-state
// counterpart.
export function removePendingInvite(
  state: PartyClientState,
  partyId: string,
): void {
  state.pendingInvites = state.pendingInvites.filter((i) => i.partyId !== partyId);
}
